/**
 * @file AgencyDropdown.js
 * @description A dropdown component for selecting an agency.
 */

import React, { useState, useEffect } from "react";
import { fetchAgencies } from "../../../services/systemService";
import { Dropdown } from "../../helpers/Dropdown";
import LoadingWrapper from "../../ui/loaders/LoadingWrapper";
import Toast from "../../ui/toasts/Toast";
import { getText } from '../../../i18n';

/**
 * Represents a dropdown component for selecting an agency.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {boolean} [props.inlineLabel=true] - Determines whether the label is displayed inline with the dropdown.
 * @param {string} [props.selectedAgency] - The ID of the selected agency.
 * @param {function} props.onAgencyChange - The callback function to be called when the selected agency changes.
 * @param {string} [props.label="Agency"] - The label text for the dropdown.
 * @param {boolean} [props.isRequired=false] - Determines whether the dropdown is required.
 * @param {boolean} [props.allowNullSelection=false] - Determines whether a null selection is allowed.
 * @returns {JSX.Element} The rendered AgencyDropdown component.
 */
const AgencyDropdown = ({
  className,
  inlineLabel = true,
  selectedAgency,
  onAgencyChange,
  label = "Agency",
  isRequired = false,
  allowNullSelection = false,
  required = false,
}) => {
  const [agencies, setAgencies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const getAgencies = async () => {
      setIsLoading(true);
      try {
        const data = await fetchAgencies();
        setAgencies(data.viewModels ?? []);
      } catch (error) {
        console.error("Could not fetch agencies:", error);
        Toast.error(getText('msg_6c3d1945a7f9'));
      } finally {
        setIsLoading(false);
      }
    };

    getAgencies();
  }, []);

  const getDefaultLabel = () => {
    const matchingAgency = agencies.find(
      (agency) => agency.id === selectedAgency
    );
    return matchingAgency ? matchingAgency.name : "Select Agency";
  };

  const agencyOptions = allowNullSelection
    ? [
        { value: null, label: getText('msg_7ddcba4f16e9') },
        ...agencies.map((agency) => ({
          value: agency.id,
          label: agency.name,
        })),
      ]
    : agencies.map((agency) => ({
        value: agency.id,
        label: agency.name,
      }));

  return (
    <div
      className={`form-group mb-3 ${
        inlineLabel ? "inline-input" : ""
      } ${className}`}
    >
      <label htmlFor="agency-dropdown">
        {label}
        {(isRequired || required) && <span className="text-danger">*</span>}
      </label>
      <LoadingWrapper isLoading={isLoading}>
        <Dropdown
          id="agency-dropdown"
          defaultValue={getDefaultLabel()}
          options={agencyOptions}
          value={selectedAgency}
          onChange={onAgencyChange}
          disabled={agencies.length === 0 || isLoading}
        />
      </LoadingWrapper>
    </div>
  );
};

export default AgencyDropdown;
